import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { APP_CONFIG } from '@application/config';

export interface ShortenedLinkResponse {
  code: string;
  shortUrl: string;
}

@Injectable({ providedIn: 'root' })
export class LinkShortenerService {
  private readonly endpoint =
    inject(APP_CONFIG).externalUrl.link_shortener_actuar;
  private readonly defaultSource = 'actuar';
  private readonly http = inject(HttpClient);
  private readonly inFlightRequests = new Map<
    string,
    Observable<ShortenedLinkResponse | null>
  >();

  /**
   * `endpoint` both issues the code and prefixes the short link, so a caller on
   * another host has to pass it — a mixed pair would hand out a dead link.
   */
  shortenLink(
    originalLink: string,
    sourceApi: string = this.defaultSource,
    endpoint: string = this.endpoint
  ): Observable<ShortenedLinkResponse | null> {
    if (!originalLink) {
      return of(null);
    }

    const requestKey = `${endpoint}::${sourceApi}::${originalLink}`;
    const inFlightRequest = this.inFlightRequests.get(requestKey);

    if (inFlightRequest) {
      return inFlightRequest;
    }

    const payload = { originalLink, sourceApi };

    const request$ = this.http
      .post<string>(endpoint, payload, {
        responseType: 'text' as 'json',
      })
      .pipe(
        map((code) => this.normalizeResponse(code, endpoint)),
        catchError(() => of(null)),
        finalize(() => {
          this.inFlightRequests.delete(requestKey);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.inFlightRequests.set(requestKey, request$);

    return request$;
  }

  private normalizeResponse(
    code: string,
    endpoint: string
  ): ShortenedLinkResponse | null {
    if (!code) {
      return null;
    }

    const normalizedCode = code.trim().replace(/^"|"$/g, '');

    if (!normalizedCode) {
      return null;
    }

    return {
      code: normalizedCode,
      shortUrl: `${endpoint}${normalizedCode}`,
    };
  }
}
